import { z } from 'zod';
import { UuidSchema } from './primitives/uuid.js';
import { createListQuerySchema, PaginationWithOffsetSchema } from './shared/pagination.js';
import { AllowedUserRoleSchema } from './user.js';

// Shared enums and constants
const USER_ROLES = ['free', 'pro', 'business', 'admin'] as const;
const USER_SORT_FIELDS = ['name', 'username', 'email', 'createdAt', 'lastSeenTimelineAt', 'role'] as const;
const ACTIVITY_FILTERS = ['all', 'active7', 'active30', 'inactive30', 'never'] as const;
const VERIFICATION_FILTERS = ['all', 'verified', 'unverified'] as const;
export const TWITTER_ACCESS_MODES = ['twitter', 'graphql', 'hybrid'] as const;

export const AdminUsersPaginationSchema = PaginationWithOffsetSchema;

const BaseAdminUsersListQuerySchema = createListQuerySchema(USER_SORT_FIELDS, {
  defaultSortBy: 'createdAt',
});

// Base user schema
export const AdminUserFeatureSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string(),
  enabled: z.boolean(),
});

export const AdminUserSchema = z.object({
  id: z.string(),
  twitterProfileId: z.string(),
  username: z.string(),
  name: z.string(),
  profileImageUrl: z.string().nullable(),
  email: z.string().nullable(),
  emailVerified: z.boolean(),
  role: z.enum(USER_ROLES),
  createdAt: z.date(),
  updatedAt: z.date(),
  isAdmin: z.boolean(),
  lastSocialSync: z.date().nullable(),
  lastSeenTimelineAt: z.date().nullable(),
  lastActivityAt: z.date().nullable(),
  features: z.array(AdminUserFeatureSchema),
  betaFeatures: z.record(z.string(), z.unknown()),
  twitterAccessModeOverride: z.enum(TWITTER_ACCESS_MODES).nullable(),
});

export type AdminUser = z.infer<typeof AdminUserSchema>;

// List/filter schemas
export const AdminUserFiltersSchema = z.object({
  role: z.enum(['all', ...USER_ROLES, 'business']).default('all'),
  activity: z.enum(ACTIVITY_FILTERS).default('all'),
  verification: z.enum(VERIFICATION_FILTERS).default('all'),
  search: z.string().trim().max(120).optional(),
  joinedFrom: z.date().optional(),
  joinedTo: z.date().optional(),
});

export type AdminUserFilters = z.infer<typeof AdminUserFiltersSchema>;

export const AdminUsersListQuerySchema = BaseAdminUsersListQuerySchema.extend({
  role: z.string().optional(),
  activity: z.string().optional(),
  verification: z.string().optional(),
  search: z.string().optional(),
  joinedFrom: z.string().optional(),
  joinedTo: z.string().optional(),
});

export type AdminUsersListQuery = z.infer<typeof AdminUsersListQuerySchema>;

export const AdminUsersListResponseSchema = z.object({
  users: z.array(AdminUserSchema),
  total: z.number().int(),
  pagination: AdminUsersPaginationSchema,
});

export type AdminUsersListResponse = z.infer<typeof AdminUsersListResponseSchema>;

// Simple list (legacy /list route)
export const AdminUsersSimpleListQuerySchema = BaseAdminUsersListQuerySchema;

export type AdminUsersSimpleListQuery = z.infer<typeof AdminUsersSimpleListQuerySchema>;

const AdminUsersSimpleListUserSchema = AdminUserSchema.pick({
  id: true,
  email: true,
  role: true,
  createdAt: true,
  updatedAt: true,
  twitterProfileId: true,
  username: true,
  name: true,
  profileImageUrl: true,
}).extend({
  id: UuidSchema,
  twitterProfileId: AdminUserSchema.shape.twitterProfileId.nullable(),
});

export const AdminUsersSimpleListResponseSchema = z.object({
  users: z.array(AdminUsersSimpleListUserSchema),
  pagination: AdminUsersPaginationSchema,
});

export type AdminUsersSimpleListResponse = z.infer<typeof AdminUsersSimpleListResponseSchema>;

// Search schemas
export const AdminUserSearchQuerySchema = z.object({
  query: z.string().trim().min(1).max(100),
  limit: z.coerce.number().int().min(1).max(25).default(10),
});

export type AdminUserSearchQuery = z.infer<typeof AdminUserSearchQuerySchema>;

export const AdminUserSearchResponseSchema = z.object({
  users: z.array(AdminUserSchema),
});

export type AdminUserSearchResponse = z.infer<typeof AdminUserSearchResponseSchema>;

// Update role schemas
export const UpdateRoleBodySchema = z.object({
  userId: UuidSchema,
  role: z.enum(USER_ROLES),
});

export type UpdateRoleBody = z.infer<typeof UpdateRoleBodySchema>;

export const UpdateRoleResponseSchema = z.object({
  user: AdminUserSchema,
});

export type UpdateRoleResponse = z.infer<typeof UpdateRoleResponseSchema>;

// Update beta features schemas
export const UpdateBetaFeaturesBodySchema = z.object({
  userId: UuidSchema,
  betaFeatures: z.unknown(),
});

export type UpdateBetaFeaturesBody = z.infer<typeof UpdateBetaFeaturesBodySchema>;

export const UpdateBetaFeaturesResponseSchema = z.object({
  user: AdminUserSchema,
});

export type UpdateBetaFeaturesResponse = z.infer<typeof UpdateBetaFeaturesResponseSchema>;

// Update subscription schemas
export const UpdateSubscriptionBodySchema = z.object({
  userId: UuidSchema,
  tierId: z.enum(['free', 'pro', 'business']),
});

export type UpdateSubscriptionBody = z.infer<typeof UpdateSubscriptionBodySchema>;

export const UpdateSubscriptionResponseSchema = z.object({
  user: AdminUserSchema,
  requestedTier: z.enum(['free', 'pro', 'business']),
  effectiveRole: z.enum(['free', 'pro']),
});

export type UpdateSubscriptionResponse = z.infer<typeof UpdateSubscriptionResponseSchema>;

// Update Twitter access mode schemas
export const UpdateTwitterAccessBodySchema = z.object({
  userId: UuidSchema,
  mode: z.enum(TWITTER_ACCESS_MODES).nullable(),
});

export type UpdateTwitterAccessBody = z.infer<typeof UpdateTwitterAccessBodySchema>;

export const UpdateTwitterAccessResponseSchema = z.object({
  userId: UuidSchema,
  mode: z.enum(TWITTER_ACCESS_MODES).nullable(),
  effectiveMode: z.enum(TWITTER_ACCESS_MODES),
});

export type UpdateTwitterAccessResponse = z.infer<typeof UpdateTwitterAccessResponseSchema>;

// Features (legacy, maps to beta features) schemas
export const UpdateFeaturesBodySchema = z.object({
  userId: UuidSchema,
  role: z.enum(USER_ROLES),
});

export type UpdateFeaturesBody = z.infer<typeof UpdateFeaturesBodySchema>;

export const UpdateFeaturesResponseSchema = z.object({
  message: z.string(),
});

export type UpdateFeaturesResponse = z.infer<typeof UpdateFeaturesResponseSchema>;

// Delete data schemas
export const DeleteDataBodySchema = z.object({
  userId: UuidSchema,
});

export type DeleteDataBody = z.infer<typeof DeleteDataBodySchema>;

export const DeleteDataResponseSchema = z.object({
  message: z.string(),
  deletedCounts: z.object({
    hashtags: z.number().int(),
    directMessages: z.number().int(),
    smartTimelineDigests: z.number().int(),
    followEvents: z.number().int(),
    userBookmarks: z.number().int(),
    userLikes: z.number().int(),
    followSnapshots: z.number().int(),
    following: z.number().int(),
    followers: z.number().int(),
    jobs: z.number().int(),
    syncJobs: z.number().int(),
    userAnalysisCache: z.number().int(),
  }),
});

export type DeleteDataResponse = z.infer<typeof DeleteDataResponseSchema>;

// Delete account schemas
export const DeleteAccountBodySchema = z.object({
  userId: UuidSchema,
});

export type DeleteAccountBody = z.infer<typeof DeleteAccountBodySchema>;

export const DeleteAccountResponseSchema = z.object({
  message: z.string(),
  deletedCounts: z.object({
    hashtags: z.number().int(),
    directMessages: z.number().int(),
    smartTimelineDigests: z.number().int(),
    followEvents: z.number().int(),
    userBookmarks: z.number().int(),
    userLikes: z.number().int(),
    followSnapshots: z.number().int(),
    following: z.number().int(),
    followers: z.number().int(),
    jobs: z.number().int(),
    syncJobs: z.number().int(),
    userAnalysisCache: z.number().int(),
    user: z.number().int(),
  }),
});

export type DeleteAccountResponse = z.infer<typeof DeleteAccountResponseSchema>;

// User stats schemas
export const UserStatsQuerySchema = z.object({
  username: z.string().trim().min(1),
});

export type UserStatsQuery = z.infer<typeof UserStatsQuerySchema>;

export const UserStatsResponseSchema = z.object({
  username: z.string(),
  userId: z.string(),
  appUserId: z.string().nullable(),
  tweets: z.object({
    authored: z.number().int(),
    mentioned: z.number().int(),
    replied: z.number().int(),
    retweeted: z.number().int(),
    quoted: z.number().int(),
    lastFetched: z.date().nullable(),
    oldestTweet: z.date().nullable(),
    newestTweet: z.date().nullable(),
  }),
  social: z.object({
    followers: z.number().int(),
    following: z.number().int(),
    followEvents: z.number().int(),
    followSnapshots: z.number().int(),
    lastSync: z.date().nullable(),
  }),
  engagement: z.object({
    likes: z.number().int(),
    bookmarks: z.number().int(),
    directMessages: z.number().int(),
  }),
  timeline: z.object({
    snapshots: z.number().int(),
    analysisReports: z.number().int(),
    lastSnapshot: z.date().nullable(),
    totalTweetsInSnapshots: z.number().int(),
  }),
  ai: z.object({
    analysisGenerated: z.number().int(),
    embeddings: z.number().int(),
    apiCallsUsed: z.number().int(),
    totalCost: z.number(),
  }),
  imports: z.object({
    archiveImports: z.number().int(),
    lastImport: z.date().nullable(),
    totalImportedItems: z.number().int(),
  }),
  timestamp: z.date(),
});

export type UserStatsResponse = z.infer<typeof UserStatsResponseSchema>;

// Timeline cache schemas
export const TimelineCacheQuerySchema = z.object({
  userId: UuidSchema,
  page: z.coerce.number().int().min(0).default(0),
  limit: z.coerce.number().int().min(1).max(200).default(50),
});

export type TimelineCacheQuery = z.infer<typeof TimelineCacheQuerySchema>;

const TimelineCacheMetadataSchema = z.record(z.string(), z.unknown()).nullable();

export const AdminTimelineCacheEntrySchema = z.object({
  id: z.string(),
  userId: z.string(),
  status: z.string(),
  timeRange: z.string(),
  createdAt: z.date(),
  completedAt: z.date().nullable(),
  fromTimestamp: z.date(),
  toTimestamp: z.date(),
  metadata: TimelineCacheMetadataSchema,
  analysis: z.unknown().nullable(),
  tweetIds: z.array(z.string()),
});

export const TimelineCacheResponseSchema = z.object({
  entries: z.array(AdminTimelineCacheEntrySchema),
  pagination: z.object({
    page: z.number().int(),
    limit: z.number().int(),
    offset: z.number().int(),
    total: z.number().int(),
  }),
});

export type TimelineCacheResponse = z.infer<typeof TimelineCacheResponseSchema>;

export const TimelineCacheDeleteQuerySchema = z.object({
  cacheId: z.string(),
});

export type TimelineCacheDeleteQuery = z.infer<typeof TimelineCacheDeleteQuerySchema>;

export const TimelineCacheDeleteResponseSchema = z.object({
  message: z.string(),
});

export type TimelineCacheDeleteResponse = z.infer<typeof TimelineCacheDeleteResponseSchema>;

// Entitlements schemas
export const UserEntitlementsPathSchema = z.object({
  userId: UuidSchema,
});

export type UserEntitlementsPath = z.infer<typeof UserEntitlementsPathSchema>;

export const UserEntitlementsResponseSchema = z.object({
  entitlements: z.object({
    role: AllowedUserRoleSchema,
    features: z
      .object({
        admin: z.boolean(),
        pro: z.boolean(),
      })
      .catchall(z.boolean()),
    isAdmin: z.boolean(),
    isPro: z.boolean(),
    limits: z.object({
      maxAnalysisPerDay: z.number().int(),
      maxTweetsPerAnalysis: z.number().int(),
      aiRequestsStandard: z.number().int(),
      aiRequestsPremium: z.number().int(),
      twitterApi: z.number().int(),
    }),
    window: z.object({
      start: z.date(),
      end: z.date(),
    }),
  }),
  quotaUsage: z.unknown().nullable(),
  tenant: z.object({
    id: z.string(),
    role: AllowedUserRoleSchema,
    isAdmin: z.boolean(),
  }),
});

export type UserEntitlementsResponse = z.infer<typeof UserEntitlementsResponseSchema>;

export const UserEntitlementsUpdateBodySchema = z.object({
  action: z.enum(['reset', 'update']),
  quotaType: z.string().optional(),
});

export type UserEntitlementsUpdateBody = z.infer<typeof UserEntitlementsUpdateBodySchema>;

export const UserEntitlementsUpdateResponseSchema = z.object({
  message: z.string(),
});

export type UserEntitlementsUpdateResponse = z.infer<typeof UserEntitlementsUpdateResponseSchema>;
